import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useSettings, waLink } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";
import QuoteForm from "@/components/QuoteForm";

const CAT_LABEL = { rice: "Rice", spices: "Indian Spices", tea: "Tea" };

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setActiveImg(0);
    api.get(`/products/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-parchment pt-40 pb-24 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-forest">Product not found</h1>
        <p className="mt-3 text-[#4A554D]">This product may have been moved or removed.</p>
        <Link to="/products" className="mt-6 inline-block bg-forest px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors">
          Back to Catalog
        </Link>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-parchment" />;
  const { product, related } = data;
  const images = product.images?.length ? product.images : [];
  const hasCerts = (product.certifications || []).length > 0;

  return (
    <div data-testid="product-detail-page">
      <SEO title={product.seo_title || `${product.name} | AnVibes Exports`} description={product.seo_description || product.short_description} path={`/products/${product.slug}`} image={images[0]} />

      <section className="bg-parchment pt-32 pb-16 lg:pt-40">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <nav className="font-mono text-[11px] uppercase tracking-[0.2em] text-forest/50" data-testid="product-breadcrumb">
            <Link to="/products" className="hover:text-gold-dark transition-colors">Products</Link>
            <span className="mx-2 text-gold">/</span>
            <Link to={`/${product.category}`} className="hover:text-gold-dark transition-colors">{CAT_LABEL[product.category]}</Link>
            <span className="mx-2 text-gold">/</span>
            <span className="text-forest">{product.name}</span>
          </nav>

          <div className="mt-8 grid gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="relative aspect-[4/3] overflow-hidden clip-frame bg-forest/5" data-testid="product-gallery">
                {images[activeImg] && (
                  <img src={images[activeImg]} alt={`${product.name} — AnVibes Exports`} className="h-full w-full object-cover" />
                )}
                <span className="absolute left-4 top-4 bg-forest/85 backdrop-blur px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">
                  {CAT_LABEL[product.category]}
                </span>
              </div>
              {images.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`gallery-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`h-20 w-24 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <Eyebrow>{CAT_LABEL[product.category]} · Export Supply</Eyebrow>
              <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-forest tracking-tight leading-[1.08]" data-testid="product-title">
                {product.name}
              </h1>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#4A554D]" data-testid="product-short-description">
                {product.short_description}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-px bg-forest/10 border border-forest/10">
                {[["Origin", product.origin], ["Grade", product.grade], ["MOQ", product.moq], ["Supply Capacity", product.supply_capacity]].map(([k, v]) => (
                  <div key={k} className="bg-white px-5 py-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest/45">{k}</p>
                    <p className="mt-1.5 text-sm font-medium text-forest">{v || "Available on request"}</p>
                  </div>
                ))}
              </div>

              {(product.forms || []).length > 0 && (
                <div className="mt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest/45">Available Forms</p>
                  <div className="mt-2.5 flex flex-wrap gap-2" data-testid="product-forms">
                    {product.forms.map((f) => (
                      <span key={f} className="border border-forest/15 bg-white px-3.5 py-1.5 text-xs font-medium text-forest">{f}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/quote?product=${encodeURIComponent(product.name)}&category=${product.category}`} data-testid="product-quote-button" className="inline-flex items-center gap-2 bg-forest px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-parchment transition-colors hover:bg-forest-leaf">
                  Request a Quote <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a href={waLink(settings, product.name)} target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-button" className="inline-flex items-center gap-2 border border-forest/20 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:border-gold hover:text-gold-dark">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
                <Link to="/contact" data-testid="product-contact-button" className="inline-flex items-center gap-2 border border-forest/20 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:border-gold hover:text-gold-dark">
                  <Mail className="h-4 w-4" /> Contact Us
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-forest/10 py-16 lg:py-20">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Reveal>
              <Eyebrow>Specifications</Eyebrow>
              <h2 className="mt-3 font-serif text-3xl text-forest">Product Specifications</h2>
              <p className="mt-4 text-sm leading-relaxed text-[#4A554D]">{product.description}</p>
              <dl className="mt-8 border border-forest/10" data-testid="product-specs-table">
                {(product.specs || []).map((s, i) => (
                  <div key={s.label} className={`grid grid-cols-2 ${i % 2 ? "bg-parchment/60" : "bg-white"}`}>
                    <dt className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-forest/55">{s.label}</dt>
                    <dd className="px-5 py-3.5 text-sm font-medium text-forest">{s.value || "Available on request"}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
          <div className="lg:col-span-2 space-y-6">
            <Reveal delay={0.1}>
              <div className="border border-forest/10 bg-parchment p-7">
                <Eyebrow>Packaging Options</Eyebrow>
                <ul className="mt-4 space-y-2.5" data-testid="product-packaging">
                  {(product.packaging || []).map((p) => (
                    <li key={p} className="flex items-center gap-2.5 text-sm text-forest">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-gold-dark" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-forest/10 bg-parchment p-7">
                <Eyebrow>Shipping Information</Eyebrow>
                <p className="mt-4 text-sm leading-relaxed text-[#4A554D]" data-testid="product-shipping-info">{product.shipping_info}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border border-forest/10 bg-parchment p-7">
                <Eyebrow>Certifications</Eyebrow>
                {hasCerts ? (
                  <ul className="mt-4 space-y-2.5">
                    {product.certifications.map((c) => (
                      <li key={c} className="flex items-center gap-2.5 text-sm text-forest">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-gold-dark" /> {c}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-[#4A554D]" data-testid="product-certifications-empty">
                    Certification details for this product are shared on request during the quotation process.
                  </p>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-24" data-testid="product-quote-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Reveal className="text-center">
            <Eyebrow>Request a Quote</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-forest">Get an export quotation for {product.name}</h2>
            <p className="mt-4 text-sm sm:text-base text-[#4A554D]">Share your quantity, packaging and destination — our export team will respond shortly.</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 border border-forest/10 bg-white p-6 sm:p-10">
            <QuoteForm defaultCategory={product.category} defaultProduct={product.name} />
          </Reveal>
        </div>
      </section>

      {(related || []).length > 0 && (
        <section className="bg-parchment pb-20 lg:pb-28" data-testid="related-products-section">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <Reveal>
              <Eyebrow>Related Products</Eyebrow>
              <h2 className="mt-3 font-serif text-3xl text-forest">More {CAT_LABEL[product.category]}</h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
